import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FieldState } from 'final-form';
import * as R from 'ramda';

import { knmErknmErrorFieldsArrangeArray } from 'KnmErknmPackage/MainModule/constants/knmErknmErrorFieldsArrangeArray';
import { knmErknmFieldsDataByFlatNameMap } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsDataByFlatNameMap';
import { knmErpErrorFieldsArrangeArray } from 'KnmErpPackage/MainModule/constants/knmErpErrorFieldsArrangeArray';
import { knmErpFieldsDataByFlatNameMap } from 'KnmErpPackage/MainModule/constants/knmErpFieldsDataByFlatNameMap';
import { knmPmErrorFieldsArrangeArray } from 'KnmPmPackage/MainModule/constants/knmPmErrorFieldsArrangeArray';
import { knmPmFieldsDataByFlatNameMap } from 'KnmPmPackage/MainModule/constants/knmPmFieldsDataByFlatNameMap';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import { knmSetErrorFields } from '../actions/knmActions';
import { getErrorFieldDataByFlatName } from '../common/getErrorFieldDataByFlatName';
import { IKnmFormRenderProps } from '../components/KnmForm';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { IKnmForm } from '../interfaces/IKnmForm';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { IErrorFieldWithProps } from '../interfaces/IErrorFieldWithProps';
import { getFieldArrayErrorMessage } from '../../../CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { getFormErrorsFlatNames } from '../../../CorePackage/MainModule/common/getFormErrorsFlatNames';
import { getNearestKnownFlatName } from '../../../CorePackage/MainModule/common/getNearestKnownFlatName';
import { getParentFlatName } from '../../../CorePackage/MainModule/common/getParentFlatName';

const errorFieldsArrangeArrayBySystemType = {
  [KnmSystemTypesEnum.ERKNM]: knmErknmErrorFieldsArrangeArray,
  [KnmSystemTypesEnum.ERP]: knmErpErrorFieldsArrangeArray,
  [KnmSystemTypesEnum.PM]: knmPmErrorFieldsArrangeArray,
};

const fieldsDataByFlatNameMapBySystemType = {
  [KnmSystemTypesEnum.ERKNM]: knmErknmFieldsDataByFlatNameMap,
  [KnmSystemTypesEnum.ERP]: knmErpFieldsDataByFlatNameMap,
  [KnmSystemTypesEnum.PM]: knmPmFieldsDataByFlatNameMap,
};

interface IUseKnmFormErrorsInitParams {
  formRef: IKnmFormRef;
  errors: IKnmFormRenderProps['errors'];
  submitFailed: IKnmFormRenderProps['submitFailed'];
  touched: IKnmFormRenderProps['touched'];
  visited: IKnmFormRenderProps['visited'];
  knmSystemType: KnmSystemTypesEnum | undefined;
  values: IKnmForm;
  settings: IAdminSetting[] | undefined;
}

export function useKnmFormErrorsInit({
  formRef,
  errors,
  submitFailed,
  touched,
  visited,
  knmSystemType,
  values,
  settings,
}: IUseKnmFormErrorsInitParams) {
  const dispatch = useDispatch();
  const knmSystemTypeRef = useRef(knmSystemType);

  knmSystemTypeRef.current = knmSystemType;

  const setErrorFields = useCallback(
    (errorFields: IErrorFieldWithProps[]) => dispatch(knmSetErrorFields(errorFields)),
    [dispatch],
  );

  const [visibleErrorsFieldsFlatNames, setVisibleErrorsFieldsFlatNames] = useState<Array<string>>([]);

  /**
   * Дополнительная проверка ошибки на необходимость ее показа сверху. Связанно с ошибкой динамического изменения типов ошибки
   */
  const validateErrorResultDecisionInspectors = (fieldName: string, fieldState: FieldState<keyof IKnmForm>) => {
    if (fieldName.indexOf('resultDecisions') > -1 && fieldName.indexOf('inspectors') > -1) {
      return typeof fieldState?.error === 'object' && !R.isEmpty(fieldState?.error);
    }

    return false;
  };

  const validateErrorTakenMeasures = (fieldName: string, fieldState: FieldState<keyof IKnmForm>) => {
    if (fieldName.indexOf('takenMeasures') > -1) {
      return typeof fieldState?.error === 'object' && fieldState?.error?.filter((e: string) => e)?.length;
    }

    return false;
  };

  const validateErrorRequirementsNpaMandatory = (fieldName: string, fieldState: FieldState<keyof IKnmForm>) => {
    if (fieldName.indexOf('requirementsNpa') > -1 && fieldName.indexOf('mandatory') > -1) {
      return getFieldArrayErrorMessage(fieldState?.error) !== undefined;
    }

    return false;
  };

  const fieldsDataByFlatNameMap = useMemo(
    () => (knmSystemType ? fieldsDataByFlatNameMapBySystemType[knmSystemType](values.created, settings) : undefined),
    [knmSystemType, values.created, settings],
  );

  useEffect(() => {
    if (!fieldsDataByFlatNameMap) return;

    setVisibleErrorsFieldsFlatNames(prev => {
      // Ошибки зарегистрированных полей - как и раньше
      const registeredFieldsErrors: Array<string> = visited
        ? Object.keys(visited)
            .filter((fieldName: keyof IKnmForm) => {
              const fieldState = formRef.current.getFieldState(fieldName) as FieldState<keyof IKnmForm>;

              return (
                typeof fieldState?.error === 'string' ||
                ((validateErrorResultDecisionInspectors(fieldName, fieldState) ||
                  validateErrorTakenMeasures(fieldName, fieldState) ||
                  validateErrorRequirementsNpaMandatory(fieldName, fieldState)) &&
                  fieldState?.touched)
              );
            })
            .map((fieldName: keyof IKnmForm) =>
              fieldName === 'typeErknm' && !formRef.current.getFieldState('kindControl')?.value?.value
                ? 'typeErknm2'
                : fieldName,
            )
        : [];

      /**
       * Ошибки полей, которых нет в final-form, KnmCollapse не рендерит children
       * до первого раскрытия, поэтому поля внутри свёрнутого аккордеона не зарегистрированы
       * и в `visited` не попадают. Из-за этого у блока была красная рамка, а иконки ошибок
       * в шапке не было. Берём их напрямую из результата валидатора всей формы -
       * из того же источника, что и рамка аккордеона (см. checkHasErrorInCollapseBlock).
       *
       * Показываем только после неудачной отправки формы, чтобы не заваливать панель
       * при первом открытии наполовину заполненного паспорта.
       */
      const collapsedBlocksErrors: Array<string> = submitFailed
        ? getFormErrorsFlatNames(errors)
            .map(flatName => getNearestKnownFlatName(flatName, fieldsDataByFlatNameMap))
            .filter((flatName): flatName is string => Boolean(flatName))
            .filter((fieldName: keyof IKnmForm) => !formRef.current.getFieldState(fieldName)?.touched)
        : [];

      const newVisibleErrorsFieldsFlatNames = R.uniq([...registeredFieldsErrors, ...collapsedBlocksErrors]);

      if (prev.join() !== newVisibleErrorsFieldsFlatNames.join()) {
        return newVisibleErrorsFieldsFlatNames;
      }

      return prev;
    });
  }, [errors, submitFailed, touched, visited, knmSystemType, formRef, fieldsDataByFlatNameMap]);

  useEffect(() => {
    const currentKnmSystemType = knmSystemTypeRef.current;

    if (currentKnmSystemType) {
      const errorFieldsWithData = visibleErrorsFieldsFlatNames.map(errorFieldFlatName => ({
        ...getErrorFieldDataByFlatName(errorFieldFlatName, formRef, currentKnmSystemType, values.created, settings),
        flatName: errorFieldFlatName,
      }));

      /**
       * Один контрол может писать сразу в несколько полей формы - например, редактор
       * описания нарушения дублирует значение в зеркальное `...violations[i].title`, -
       * и валидатор возвращает ошибку на каждое из них. В панели это давало две
       * одинаковые строки, причём у зеркального поля нет якоря в разметке и по нему
       * не было скролла. Одинаковый заголовок в пределах одного элемента списка -
       * это для пользователя одна и та же строка, оставляем первую.
       */
      const errorFieldsDeduped = R.uniqBy(
        errorField => `${getParentFlatName(errorField.flatName)}|${errorField.title}`,
        errorFieldsWithData,
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorFieldsArrangeArray: any = errorFieldsArrangeArrayBySystemType[currentKnmSystemType];

      const errorFieldsWithDataSorted = errorFieldsDeduped.sort(
        (a, b) => errorFieldsArrangeArray.indexOf(a.nameTemplate) - errorFieldsArrangeArray.indexOf(b.nameTemplate),
      );

      setErrorFields(errorFieldsWithDataSorted);
    }
  }, [formRef, setErrorFields, visibleErrorsFieldsFlatNames, values.created, settings]);
}
